'use client';
import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { useSession } from '@web/api';
import { useSessionCustomer } from '@web/customer';
import { LoadingView, Footer } from '@web/components';
import { Redirect } from '@web/router';
import { MarketingHeader } from '../../cms';
import { ScreeningStepContainer } from '../ScreeningStepContainer';
import { PupInfoFormContainer } from '../PupInfoFormContainer';
import { UploadImageContainer } from '../UploadImageContainer';
import { EmailFormContainer } from '../EmailFormContainer';
import { useHealthScreening, HealthScreeningProvider } from './context';
import {
  VALID_WEIGHT_SUBPATHS,
  WEIGHT_ROOT,
  PUP_INFO_SUBPATH,
  UPLOAD_PHOTO_SUBPATH,
  EMAIL_SUBPATH,
} from './constants';
import { StyledHealthScreeningFlowContainer, ContentContainer } from './styles';

const STEPS = {
  pup: 'pup',
  photo: 'photo',
  email: 'email',
};

const components = {
  [STEPS.pup]: PupInfoFormContainer,
  [STEPS.photo]: UploadImageContainer,
  [STEPS.email]: EmailFormContainer,
};

// This component is currently written with only Weight screenings in mind. It will have to be refactored
// when including additional screenings on the web.
const HealthScreeningFlow = ({ content }) => {
  const { pet, imageId } = useHealthScreening();
  const { user, isLoading: isSessionLoading } = useSession();
  const { isActive } = useSessionCustomer();
  const router = useRouter();
  const [slug] = router.query.path ?? [];
  const pathname = slug ?? PUP_INFO_SUBPATH;
  const lastComponentRef = useRef({ component: LoadingView, props: {} });
  useEffect(() => {
    if (slug === UPLOAD_PHOTO_SUBPATH && pet.name === '') {
      Sentry.addBreadcrumb({
        message: 'WoW redirect to pup info from photo',
        ...pet,
      });
      router.push(`${WEIGHT_ROOT}${PUP_INFO_SUBPATH}`);
    } else if (slug === EMAIL_SUBPATH) {
      if (pet.name === '') {
        router.push(`${WEIGHT_ROOT}${PUP_INFO_SUBPATH}`);
      } else if (!imageId) {
        router.push(`${WEIGHT_ROOT}${UPLOAD_PHOTO_SUBPATH}`);
      }
    }
  }, [router, slug, pet, imageId]);
  let props = content;
  let Component = components[pathname];

  if (!VALID_WEIGHT_SUBPATHS.includes(pathname) || !Component) {
    Component = lastComponentRef.current.component;
    props = lastComponentRef.current.props;
  } else lastComponentRef.current = { component: Component, props };

  if (isSessionLoading) return <LoadingView />;
  if (user?.id && isActive) {
    return <Redirect to="/screening-ready/" />;
  }

  return (
    <StyledHealthScreeningFlowContainer>
      <ContentContainer>
        <ScreeningStepContainer
          didYouKnowInfo={content?.didYouKnowInfo}
          tips={content?.tips}
        >
          <Component {...props} />
        </ScreeningStepContainer>
      </ContentContainer>
    </StyledHealthScreeningFlowContainer>
  );
};

HealthScreeningFlow.displayName = 'HealthScreeningFlow';
HealthScreeningFlow.propTypes = {
  content: PropTypes.object,
};

export const HealthScreeningContainer = ({ content }) => (
  <HealthScreeningProvider>
    <MarketingHeader links={content.header.links} />
    <HealthScreeningFlow content={content} />
    <Footer {...content.footer} />
  </HealthScreeningProvider>
);

HealthScreeningContainer.displayName = 'HealthScreeningContainer';
HealthScreeningContainer.propTypes = {
  content: PropTypes.shape({
    header: PropTypes.object.isRequired,
    body: PropTypes.arrayOf(PropTypes.object),
    footer: PropTypes.object.isRequired,
  }).isRequired,
};
